import React, { useState, useMemo, useCallback, useEffect, useReducer } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Package,
  Eye,
  Clock,
  Check,
  MinusCircle,
  Plus,
  X,
  CheckCircle,
  XCircle,
  Search,
  Filter,
  ArrowLeft,
  ArrowRight,
  RefreshCw,
  ChevronDown,
  AlertCircle,
  ChevronUp,
  Calendar,
  Tag,
  Box,
  Archive,
  Info,
  AlertTriangle,
  ShieldCheck,
  ShieldX,
  Layers
} from 'lucide-react';
import { returnsAPI, inventoryAPI } from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useSocket } from '../contexts/SocketContext';
import { useNotifications } from '../contexts/NotificationContext';
import { CustomDropdown } from '../components/UI/CustomDropdown';

enum ReturnStatus {
  PENDING = 'pending_approval',
  APPROVED = 'approved',
  REJECTED = 'rejected',
}

enum ReturnReason {
  DAMAGED_AR = 'تالف',
  WRONG_ITEM_AR = 'منتج خاطئ',
  EXCESS_QUANTITY_AR = 'كمية زائدة',
  OTHER_AR = 'أخرى',
  DAMAGED_EN = 'Damaged',
  WRONG_ITEM_EN = 'Wrong Item',
  EXCESS_QUANTITY_EN = 'Excess Quantity',
  OTHER_EN = 'Other',
}

interface Branch {
  _id: string;
  name: string;
  nameEn: string;
  displayName: string;
}

interface ReturnItem {
  productId: string;
  quantity: number;
  reason: string;
  reasonEn: string;
  maxQuantity: number;
  product?: {
    name: string;
    nameEn: string;
    unit: string;
    unitEn: string;
    price: number;
  };
}

interface Return {
  _id: string;
  returnNumber: string;
  branch: Branch | null;
  items: Array<{
    itemId: string;
    product: {
      _id: string;
      name: string;
      nameEn: string;
      unit: string;
      unitEn: string;
      displayName: string;
      displayUnit: string;
      department: { name: string; nameEn: string; displayName: string } | null;
      price: number;
    };
    quantity: number;
    reason: string;
    reasonEn: string;
    approvedQuantity?: number;
    isRejected?: boolean;
  }>;
  status: ReturnStatus;
  createdAt: string;
  notes: string;
  reviewNotes: string;
  createdBy?: {
    name: string;
    nameEn: string;
  };
  reviewedBy?: {
    name: string;
    nameEn: string;
  };
}

interface AvailableItem {
  productId: string;
  productName: string;
  available: number;
  unit: string;
  displayUnit: string;
  departmentName: string;
  stock: number;
  price: number;
}

interface ReturnFormState {
  notes: string;
  items: ReturnItem[];
}

interface ItemApproval {
  itemId: string;
  approvedQuantity: number;
  status: 'approved' | 'rejected' | 'partial';
}

// واجهة جديدة لمودال تأكيد الموافقة بالقوة
interface ForceApproveModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (force: boolean) => void;
  t: any;
  isRtl: boolean;
  errorMessage?: string;
}

const ForceApproveModal: React.FC<ForceApproveModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  t,
  isRtl,
  errorMessage,
}) => {
  const [useAvailableOnly, setUseAvailableOnly] = useState(true);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[100] p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.95, opacity: 0, y: 20 }}
            className="bg-white rounded-2xl w-full max-w-md overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              <div className="flex items-center gap-3 mb-5">
                <div className="p-2.5 bg-amber-100 rounded-full">
                  <AlertTriangle className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {t.confirmPartialApproval}
                  </h3>
                  <p className="text-xs text-gray-500 mt-1">{t.attentionRequired}</p>
                </div>
              </div>
              
              <div className="mb-6">
                <div className="bg-red-50 border border-red-100 rounded-xl p-4 mb-4">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-red-700 font-medium">
                      {errorMessage || t.errors.insufficientQuantity}
                    </p>
                  </div>
                </div>
                
                <div className="bg-amber-50 border border-amber-100 rounded-xl p-4 mb-4">
                  <p className="text-sm font-medium text-amber-800 mb-3 flex items-center gap-2">
                    <Info className="w-4 h-4" />
                    {t.partialApprovalOptions}
                  </p>
                  
                  <div className="space-y-3">
                    <label className="flex items-start gap-3 cursor-pointer group">
                      <div className="relative mt-0.5">
                        <input
                          type="radio"
                          checked={useAvailableOnly}
                          onChange={() => setUseAvailableOnly(true)}
                          className="sr-only peer"
                        />
                        <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center transition-all duration-200 ${
                          useAvailableOnly 
                            ? 'border-amber-600 bg-amber-600' 
                            : 'border-gray-300 group-hover:border-amber-500'
                        }`}>
                          {useAvailableOnly && (
                            <div className="w-1.5 h-1.5 rounded-full bg-white"></div>
                          )}
                        </div>
                      </div>
                      <div className="flex-1">
                        <span className="text-sm font-medium text-gray-800 block">
                          {t.useAvailableQuantity}
                        </span>
                        <p className="text-xs text-gray-600 mt-1.5 leading-relaxed">
                          {t.useAvailableQuantityDesc}
                        </p>
                      </div>
                    </label>
                    
                    <label className="flex items-start gap-3 cursor-pointer group">
                      <div className="relative mt-0.5">
                        <input
                          type="radio"
                          checked={!useAvailableOnly}
                          onChange={() => setUseAvailableOnly(false)}
                          className="sr-only peer"
                        />
                        <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center transition-all duration-200 ${
                          !useAvailableOnly 
                            ? 'border-red-600 bg-red-600' 
                            : 'border-gray-300 group-hover:border-red-500'
                        }`}>
                          {!useAvailableOnly && (
                            <div className="w-1.5 h-1.5 rounded-full bg-white"></div>
                          )}
                        </div>
                      </div>
                      <div className="flex-1">
                        <span className="text-sm font-medium text-gray-800 block">
                          {t.forceApproveAll}
                        </span>
                        <p className="text-xs text-gray-600 mt-1.5 leading-relaxed">
                          {t.forceApproveAllDesc}
                        </p>
                      </div>
                    </label>
                  </div>
                </div>
                
                <div className="bg-blue-50 border border-blue-100 rounded-xl p-3">
                  <div className="flex items-start gap-2">
                    <Info className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                    <p className="text-xs text-blue-700 leading-relaxed">
                      <span className="font-medium">{t.note}:</span> {t.partialApprovalNote}
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
                <button
                  onClick={onClose}
                  className="px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-all duration-200 text-sm active:scale-95"
                >
                  {t.common.cancel}
                </button>
                <button
                  onClick={() => onConfirm(!useAvailableOnly)}
                  className={`px-4 py-2.5 font-medium rounded-lg transition-all duration-200 text-sm active:scale-95 ${
                    useAvailableOnly
                      ? 'bg-amber-600 hover:bg-amber-700 text-white shadow-sm'
                      : 'bg-red-600 hover:bg-red-700 text-white shadow-sm'
                  }`}
                >
                  {useAvailableOnly ? t.confirmWithAvailable : t.confirmForceApprove}
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// Component for quantity input - محسن ومصغر
interface QuantityInputProps {
  value: number;
  onChange: (val: number) => void;
  min?: number;
  max?: number;
  unit: string;
  disabled?: boolean;
  t: any;
  isRtl: boolean;
  productUnit: string;
}

const QuantityInput: React.FC<QuantityInputProps> = ({
  value,
  onChange,
  min = 1,
  max,
  unit,
  disabled = false,
  t,
  isRtl,
  productUnit,
}) => {
  const [inputValue, setInputValue] = useState<string>(value.toFixed(1));

  const isKg = /كيلو|كجم|kg|kilo/i.test(productUnit);
  const actualMin = isKg ? 0.1 : 1;
  const step = isKg ? 0.1 : 1;

  const handleIncrement = () => {
    const newValue = parseFloat((value + step).toFixed(1));
    if (max !== undefined && newValue > max) {
      toast.error(t.errors.invalidQuantityMax.replace('{min}', actualMin.toString()).replace('{max}', max.toString()));
      return;
    }
    onChange(newValue);
    setInputValue(newValue.toFixed(1));
  };

  const handleDecrement = () => {
    const newValue = parseFloat(Math.max(actualMin, value - step).toFixed(1));
    onChange(newValue);
    setInputValue(newValue.toFixed(1));
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setInputValue(val);
  };

  const handleInputBlur = () => {
    let num = parseFloat(inputValue);
    if (isNaN(num)) {
      setInputValue(value.toFixed(1));
      return;
    }

    // تقريب إلى منزلة عشرية واحدة
    num = parseFloat(num.toFixed(1));

    // التحقق من الحد الأدنى
    if (num < actualMin) {
      toast.error(t.errors.invalidQuantityMin.replace('{min}', actualMin.toString()));
      setInputValue(value.toFixed(1));
      return;
    }

    // التحقق من الحد الأقصى
    if (max !== undefined && num > max) {
      toast.error(t.errors.invalidQuantityMax.replace('{min}', actualMin.toString()).replace('{max}', max.toString()));
      setInputValue(value.toFixed(1));
      return;
    }

    onChange(num);
    setInputValue(num.toFixed(1));
  };

  useEffect(() => {
    setInputValue(value.toFixed(1));
  }, [value]);

  return (
    <div className={`flex items-center h-9 ${isRtl ? 'flex-row' : ''}`}>
      <button
        type="button"
        onClick={handleDecrement}
        disabled={disabled || value <= actualMin}
        className={`w-8 h-full flex items-center justify-center rounded-l-lg border border-gray-300 transition-all duration-200 ${
          disabled || value <= actualMin
            ? 'bg-gray-50 text-gray-400 cursor-not-allowed'
            : 'bg-white hover:bg-gray-50 text-gray-700 active:bg-gray-100'
        }`}
        aria-label={t.common.decrease}
      >
        <MinusCircle className="w-3.5 h-3.5" />
      </button>
      
      <div className="relative flex-1 h-full">
        <input
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          disabled={disabled}
          className="w-full h-full text-center border-y border-gray-300 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm px-2"
          dir="ltr"
        />
        <span className="absolute inset-y-0 right-2 flex items-center text-xs text-gray-500">
          {unit}
        </span>
      </div>
      
      <button
        type="button"
        onClick={handleIncrement}
        disabled={disabled || (max !== undefined && value >= max)}
        className={`w-8 h-full flex items-center justify-center rounded-r-lg border border-gray-300 transition-all duration-200 ${
          disabled || (max !== undefined && value >= max)
            ? 'bg-gray-50 text-gray-400 cursor-not-allowed'
            : 'bg-amber-600 hover:bg-amber-700 text-white active:bg-amber-800'
        }`}
        aria-label={t.common.increase}
      >
        <Plus className="w-3.5 h-3.5" />
      </button>
    </div>
  );
};

// Component for approval quantity input - محسن ومصغر مع تمكين الزيادة
interface ApprovalQuantityInputProps {
  value: number;
  onChange: (val: number) => void;
  original: number;
  unit: string;
  disabled?: boolean;
  t: any;
  isRtl: boolean;
  productUnit: string;
  allowExceedOriginal?: boolean; // إضافة خاصية للسماح بتجاوز الكمية الأصلية
}

const ApprovalQuantityInput: React.FC<ApprovalQuantityInputProps> = ({
  value,
  onChange,
  original,
  unit,
  disabled = false,
  t,
  isRtl,
  productUnit,
  allowExceedOriginal = true, // السماح بتجاوز الكمية الأصلية افتراضياً
}) => {
  const [inputValue, setInputValue] = useState<string>(value.toFixed(1));

  const isKg = /كيلو|كجم|kg|kilo/i.test(productUnit);
  const min = isKg ? 0.1 : 1;
  const step = isKg ? 0.1 : 1;
  const max = allowExceedOriginal ? undefined : original; // السماح بتجاوز الكمية الأصلية إذا كان مسموحاً

  const handleIncrement = () => {
    const newValue = parseFloat((value + step).toFixed(1));
    if (max !== undefined && newValue > max) {
      toast.error(t.errors.invalidQuantityMax.replace('{min}', min.toString()).replace('{max}', max.toString()));
      return;
    }
    onChange(newValue);
    setInputValue(newValue.toFixed(1));
  };

  const handleDecrement = () => {
    const newValue = parseFloat(Math.max(min, value - step).toFixed(1));
    onChange(newValue);
    setInputValue(newValue.toFixed(1));
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setInputValue(val);
  };

  const handleInputBlur = () => {
    let num = parseFloat(inputValue);
    if (isNaN(num)) {
      setInputValue(value.toFixed(1));
      return;
    }

    num = parseFloat(num.toFixed(1));

    if (num < min) {
      toast.error(t.errors.invalidQuantityMin.replace('{min}', min.toString()));
      setInputValue(value.toFixed(1));
      return;
    }

    if (max !== undefined && num > max) {
      toast.error(t.errors.invalidQuantityMax.replace('{min}', min.toString()).replace('{max}', max.toString()));
      setInputValue(value.toFixed(1));
      return;
    }

    onChange(num);
    setInputValue(num.toFixed(1));
  };

  useEffect(() => {
    setInputValue(value.toFixed(1));
  }, [value]);

  // تحديد حالة الزيادة لتغيير لون الزر
  const isExceedingOriginal = value > original;

  return (
    <div className="space-y-1.5">
      <div className={`flex items-center h-9 ${isRtl ? 'flex-row' : ''}`}>
        <button
          type="button"
          onClick={handleDecrement}
          disabled={disabled || value <= min}
          className={`w-8 h-full flex items-center justify-center rounded-l-lg border border-gray-300 transition-all duration-200 ${
            disabled || value <= min
              ? 'bg-gray-50 text-gray-400 cursor-not-allowed'
              : 'bg-white hover:bg-gray-50 text-gray-700 active:bg-gray-100'
          }`}
          aria-label={t.common.decrease}
        >
          <MinusCircle className="w-3.5 h-3.5" />
        </button>
        
        <div className="relative flex-1 h-full">
          <input
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            onBlur={handleInputBlur}
            disabled={disabled}
            className={`w-full h-full text-center border-y border-gray-300 focus:outline-none focus:ring-2 focus:border-transparent text-sm px-2 ${
              isExceedingOriginal 
                ? 'focus:ring-purple-500 border-purple-300' 
                : 'focus:ring-blue-500'
            }`}
            dir="ltr"
          />
          <span className="absolute inset-y-0 right-2 flex items-center text-xs text-gray-500">
            {unit}
          </span>
        </div>
        
        <button
          type="button"
          onClick={handleIncrement}
          disabled={disabled || (max !== undefined && value >= max)}
          className={`w-8 h-full flex items-center justify-center rounded-r-lg border border-gray-300 transition-all duration-200 ${
            disabled || (max !== undefined && value >= max)
              ? 'bg-gray-50 text-gray-400 cursor-not-allowed'
              : isExceedingOriginal
                ? 'bg-purple-600 hover:bg-purple-700 text-white active:bg-purple-800'
                : 'bg-blue-600 hover:bg-blue-700 text-white active:bg-blue-800'
          }`}
          aria-label={t.common.increase}
        >
          <Plus className="w-3.5 h-3.5" />
        </button>
      </div>
      
      {isExceedingOriginal && (
        <div className="flex items-center gap-1 text-xs text-purple-600">
          <Layers className="w-3 h-3" />
          <span>{t.quantityIncreased}</span>
          <span className="font-bold">+{(value - original).toFixed(1)}</span>
        </div>
      )}
    </div>
  );
};

// Reducer for form state
type ReturnFormAction =
  | { type: 'SET_NOTES'; payload: string }
  | { type: 'ADD_ITEM'; payload: ReturnItem }
  | { type: 'UPDATE_ITEM'; payload: { index: number; field: keyof ReturnItem; value: any } }
  | { type: 'REMOVE_ITEM'; payload: number }
  | { type: 'RESET' }
  | { type: 'SET_ITEMS'; payload: ReturnItem[] };

const returnFormReducer = (state: ReturnFormState, action: ReturnFormAction): ReturnFormState => {
  switch (action.type) {
    case 'SET_NOTES':
      return { ...state, notes: action.payload };
    case 'ADD_ITEM':
      return { ...state, items: [...state.items, action.payload] };
    case 'UPDATE_ITEM':
      const newItems = [...state.items];
      const index = action.payload.index;
      if (newItems[index]) {
        newItems[index] = { ...newItems[index], [action.payload.field]: action.payload.value };
      }
      return { ...state, items: newItems };
    case 'REMOVE_ITEM':
      return { ...state, items: state.items.filter((_, i) => i !== action.payload) };
    case 'SET_ITEMS':
      return { ...state, items: action.payload };
    case 'RESET':
      return { notes: '', items: [] };
    default:
      return state;
  }
};

// Modal for approving returns - محسن جداً مع تمكين الزيادة
interface ApproveReturnModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedReturn: Return | null;
  t: any;
  isRtl: boolean;
  onSubmit: (approvedItems: ItemApproval[], reviewNotes: string, status: ReturnStatus, forceApprove?: boolean) => Promise<void>;
  isSubmitting: boolean;
}

const ApproveReturnModal: React.FC<ApproveReturnModalProps> = ({
  isOpen,
  onClose,
  selectedReturn,
  t,
  isRtl,
  onSubmit,
  isSubmitting,
}) => {
  const [reviewNotes, setReviewNotes] = useState('');
  const [itemApprovals, setItemApprovals] = useState<ItemApproval[]>([]);
  const [actionType, setActionType] = useState<'approve' | 'reject'>('approve');
  const [showQuantityIncrease, setShowQuantityIncrease] = useState<boolean>(true); // التحكم في إظهار خيار الزيادة

  useEffect(() => {
    if (selectedReturn && isOpen) {
      setItemApprovals(
        selectedReturn.items.map(item => ({
          itemId: item.itemId || item.product._id,
          approvedQuantity: item.quantity,
          status: 'approved' as const
        }))
      );
      setReviewNotes('');
      setActionType('approve');
    }
  }, [selectedReturn, isOpen]);

  const handleItemApproval = (itemId: string, quantity: number, status: 'approved' | 'rejected' | 'partial') => {
    setItemApprovals(prev => 
      prev.map(item => 
        item.itemId === itemId 
          ? { ...item, approvedQuantity: quantity, status }
          : item
      )
    );
  };

  const handleApproveAll = () => {
    if (!selectedReturn) return;
    setItemApprovals(
      selectedReturn.items.map(item => ({
        itemId: item.itemId || item.product._id,
        approvedQuantity: item.quantity,
        status: 'approved'
      }))
    );
    setActionType('approve');
  };

  const handleRejectAll = () => {
    if (!selectedReturn) return;
    setItemApprovals(
      selectedReturn.items.map(item => ({
        itemId: item.itemId || item.product._id,
        approvedQuantity: 0,
        status: 'rejected'
      }))
    );
    setActionType('reject');
  };

  const handleIncreaseAll = () => {
    if (!selectedReturn) return;
    setItemApprovals(
      selectedReturn.items.map(item => ({
        itemId: item.itemId || item.product._id,
        approvedQuantity: item.quantity + (item.product.unit.includes('كيلو') ? 0.5 : 1),
        status: 'approved'
      }))
    );
    setActionType('approve');
  };

  const handleResetItem = (itemId: string) => {
    if (!selectedReturn) return;
    const item = selectedReturn.items.find(i => (i.itemId || i.product._id) === itemId);
    if (item) {
      handleItemApproval(itemId, item.quantity, 'approved');
    }
  };

  const handleSubmit = async () => {
    if (!selectedReturn) return;

    const hasApprovedItem = itemApprovals.some(item => item.status === 'approved' && item.approvedQuantity > 0);
    const hasPartialItem = itemApprovals.some(item => item.status === 'partial');
    
    if (!hasApprovedItem && !hasPartialItem) {
      toast.error(t.errors.approvalRequired);
      return;
    }

    const finalStatus = actionType === 'approve' ? ReturnStatus.APPROVED : ReturnStatus.REJECTED;
    
    try {
      await onSubmit(itemApprovals, reviewNotes, finalStatus, false);
    } catch (error) {
      // سيتم التعامل مع الخطأ في الدالة الأم
    }
  };

  if (!isOpen || !selectedReturn) return null;

  const approvedItems = itemApprovals.filter(item => item.status === 'approved' && item.approvedQuantity > 0);
  const rejectedItems = itemApprovals.filter(item => item.status === 'rejected' || item.approvedQuantity === 0);
  const partialItems = itemApprovals.filter(item => 
    item.status === 'partial' || 
    (item.approvedQuantity > 0 && item.approvedQuantity < selectedReturn.items.find(i => (i.itemId || i.product._id) === item.itemId)?.quantity)
  );

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.95, opacity: 0, y: 20 }}
            className="bg-white rounded-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-5 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <ShieldCheck className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900">
                      {t.approveModalTitle.replace('{returnNumber}', selectedReturn.returnNumber)}
                    </h2>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded-full">
                        {t.branch}: {selectedReturn.branch?.displayName}
                      </span>
                      <span className="text-xs px-2 py-1 bg-amber-100 text-amber-700 rounded-full">
                        {selectedReturn.items.length} {t.items}
                      </span>
                    </div>
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-gray-100 rounded-xl transition-all duration-200 active:scale-95"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-5">
              {/* إضافة خيار التحكم في الزيادة */}
              <div className="mb-5 p-3 bg-blue-50 rounded-xl border border-blue-100">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Layers className="w-4 h-4 text-blue-600" />
                    <span className="text-sm font-medium text-gray-800">
                      {t.allowQuantityIncrease}
                    </span>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={showQuantityIncrease}
                      onChange={(e) => setShowQuantityIncrease(e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>
                <p className="text-xs text-blue-600 mt-2">
                  {t.allowQuantityIncreaseDesc}
                </p>
              </div>

              <div className="space-y-3 mb-5">
                {selectedReturn.items.map((item, index) => {
                  const itemId = item.itemId || item.product._id;
                  const approval = itemApprovals.find(a => a.itemId === itemId);
                  const approvedQty = approval?.approvedQuantity || 0;
                  const isApproved = approval?.status === 'approved' && approvedQty > 0;
                  const isRejected = approval?.status === 'rejected' || approvedQty === 0;
                  const isPartial = approval?.status === 'partial' || 
                    (approvedQty > 0 && approvedQty < item.quantity);
                  const isIncreased = approvedQty > item.quantity;
                  const isKg = /كيلو|كجم|kg|kilo/i.test(item.product.unit);

                  let bgColor = 'bg-white';
                  let borderColor = 'border-gray-200';
                  if (isRejected) {
                    bgColor = 'bg-red-50/50';
                    borderColor = 'border-red-200';
                  } else if (isIncreased) {
                    bgColor = 'bg-purple-50/50';
                    borderColor = 'border-purple-200';
                  } else if (isApproved && approvedQty === item.quantity) {
                    bgColor = 'bg-green-50/50';
                    borderColor = 'border-green-200';
                  } else if (isPartial) {
                    bgColor = 'bg-amber-50/50';
                    borderColor = 'border-amber-200';
                  }

                  return (
                    <div
                      key={itemId}
                      className={`rounded-xl border ${borderColor} ${bgColor} p-4 transition-all duration-200`}
                    >
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="font-medium text-sm text-gray-800 truncate">
                              {item.product.displayName}
                            </span>
                            {isRejected ? (
                              <XCircle className="flex-shrink-0 w-4 h-4 text-red-600" />
                            ) : isIncreased ? (
                              <Layers className="flex-shrink-0 w-4 h-4 text-purple-600" />
                            ) : isPartial ? (
                              <AlertCircle className="flex-shrink-0 w-4 h-4 text-amber-600" />
                            ) : isApproved ? (
                              <CheckCircle className="flex-shrink-0 w-4 h-4 text-green-600" />
                            ) : null}
                          </div>
                          <div className="text-xs text-gray-600 space-y-1.5">
                            <div className="flex items-center gap-3 flex-wrap">
                              <span className="bg-gray-100 px-2 py-0.5 rounded">
                                {t.originalQuantity}: {item.quantity.toFixed(1)} {item.product.displayUnit}
                              </span>
                              <span className="text-gray-500">
                                {t.reason}: {isRtl ? item.reason : item.reasonEn}
                              </span>
                              {isIncreased && (
                                <span className="bg-purple-100 text-purple-700 px-2 py-0.5 rounded">
                                  {t.quantityIncreased}: +{(approvedQty - item.quantity).toFixed(1)} {item.product.displayUnit}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="flex flex-col items-end gap-3">
                          <div className="flex gap-2">
                            <button
                              type="button"
                              onClick={() => handleItemApproval(
                                itemId, 
                                0, 
                                'rejected'
                              )}
                              className={`px-2.5 py-1.5 text-xs rounded-lg transition-all duration-200 active:scale-95 ${
                                isRejected
                                  ? 'bg-red-100 text-red-700'
                                  : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                              }`}
                            >
                              <ShieldX className="w-3.5 h-3.5 inline mr-1" />
                              {t.rejectItem}
                            </button>
                          </div>
                          
                          <div className="flex items-center gap-3">
                            <span className="text-xs text-gray-500 font-medium">{t.approvedQuantity}:</span>
                            <ApprovalQuantityInput
                              value={approvedQty}
                              onChange={(val) => handleItemApproval(
                                itemId, 
                                val, 
                                val === 0 ? 'rejected' : 
                                val === item.quantity ? 'approved' : 'partial'
                              )}
                              original={item.quantity}
                              unit={item.product.displayUnit}
                              t={t}
                              isRtl={isRtl}
                              productUnit={item.product.unit}
                              allowExceedOriginal={showQuantityIncrease}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="flex mb-5 justify-end items-end gap-2">
                <button
                  type="button"
                  onClick={handleApproveAll}
                  className="px-3.5 py-2 bg-green-50 hover:bg-green-100 text-green-700 rounded-xl text-xs font-medium transition-all duration-200 flex items-center gap-1.5 active:scale-95"
                >
                  <CheckCircle className="w-4 h-4" />
                  {t.approveAll}
                </button>
                {showQuantityIncrease && (
                  <button
                    type="button"
                    onClick={handleIncreaseAll}
                    className="px-3.5 py-2 bg-purple-50 hover:bg-purple-100 text-purple-700 rounded-xl text-xs font-medium transition-all duration-200 flex items-center gap-1.5 active:scale-95"
                  >
                    <Layers className="w-4 h-4" />
                    {t.increaseAll}
                  </button>
                )}
                <button
                  type="button"
                  onClick={handleRejectAll}
                  className="px-3.5 py-2 bg-red-50 hover:bg-red-100 text-red-700 rounded-xl text-xs font-medium transition-all duration-200 flex items-center gap-1.5 active:scale-95"
                >
                  <ShieldX className="w-4 h-4" />
                  {t.rejectAll}
                </button>
              </div>

              <div className="mb-5">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <div className="flex items-center gap-2">
                    <Tag className="w-4 h-4 text-gray-500" />
                    {t.reviewNotes}
                  </div>
                </label>
                <textarea
                  value={reviewNotes}
                  onChange={(e) => setReviewNotes(e.target.value)}
                  placeholder={t.reviewNotesPlaceholder}
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none text-sm"
                />
              </div>
            </div>

            <div className="p-5 border-t border-gray-200 bg-gray-50">
              <div className="flex items-center align-end justify-end">
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={onClose}
                    className="px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-medium transition-all duration-200 text-sm active:scale-95"
                  >
                    {t.common.cancel}
                  </button>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        setActionType('reject');
                        handleSubmit();
                      }}
                      disabled={isSubmitting}
                      className="px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-xl font-medium transition-all duration-200 text-sm disabled:opacity-50 disabled:cursor-not-allowed active:scale-95 flex items-center gap-2"
                    >
                      {isSubmitting && actionType === 'reject' ? (
                        <RefreshCw className="w-4 h-4 animate-spin" />
                      ) : (
                        <ShieldX className="w-4 h-4" />
                      )}
                      {t.confirmRejection}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setActionType('approve');
                        handleSubmit();
                      }}
                      disabled={isSubmitting}
                      className="px-4 py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-xl font-medium transition-all duration-200 text-sm disabled:opacity-50 disabled:cursor-not-allowed active:scale-95 flex items-center gap-2"
                    >
                      {isSubmitting && actionType === 'approve' ? (
                        <RefreshCw className="w-4 h-4 animate-spin" />
                      ) : (
                        <ShieldCheck className="w-4 h-4" />
                      )}
                      {t.confirmApproval}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// Translations object - محدث مع إضافات جديدة للزيادة
const translations = {
  ar: {
    title: 'إدارة المرتجعات',
    subtitle: 'إنشاء ومتابعة وإدارة طلبات الإرجاع',
    noReturns: 'لا توجد مرتجعات',
    returnNumber: 'رقم الإرجاع',
    statusLabel: 'الحالة',
    date: 'التاريخ',
    totalQuantity: 'إجمالي الكمية',
    totalPrice: 'إجمالي السعر',
    branch: 'الفرع',
    selectBranch: 'اختر الفرع',
    notesLabel: 'ملاحظات',
    reviewNotes: 'ملاحظات المراجعة',
    noNotes: 'لا توجد ملاحظات',
    createReturn: 'إنشاء مرتجع جديد',
    viewReturn: 'عرض المرتجع',
    approveReturn: 'الموافقة على المرتجع',
    rejectReturn: 'رفض المرتجع',
    view: 'عرض',
    searchPlaceholder: 'بحث برقم الإرجاع أو المنتج...',
    filterStatus: 'تصفية حسب الحالة',
    allStatuses: 'جميع الحالات',
    selectProduct: 'اختر منتج',
    reason: 'سبب الإرجاع',
    selectReason: 'اختر السبب',
    damaged: 'تالف',
    wrongItem: 'منتج خاطئ',
    excessQuantity: 'كمية زائدة',
    other: 'أخرى',
    notesPlaceholder: 'ملاحظات إضافية (اختياري)',
    reviewNotesPlaceholder: 'ملاحظات المراجعة (اختياري)',
    items: 'العناصر',
    addItem: 'إضافة عنصر',
    removeItem: 'إزالة العنصر',
    submitReturn: 'إرسال المرتجع',
    approve: 'موافقة',
    reject: 'رفض',
    approveAll: 'قبول الكل',
    rejectAll: 'رفض الكل',
    increaseAll: 'زيادة الكل',
    partialApprove: 'قبول جزئي',
    createSuccess: 'تم إنشاء المرتجع بنجاح',
    updateSuccess: 'تم تحديث حالة المرتجع بنجاح',
    newReturnNotification: 'تم إنشاء مرتجع جديد: {returnNumber}',
    quantity: 'الكمية',
    unit: 'الوحدة',
    department: 'القسم',
    product: 'المنتج',
    price: 'السعر',
    currency: 'ريال',
    status: {
      pending: 'قيد المراجعة',
      approved: 'تمت الموافقة',
      rejected: 'مرفوض',
      all: 'جميع الحالات',
    },
    actions: 'الإجراءات',
    createdAt: 'تاريخ الإنشاء',
    createdBy: 'تم الإنشاء بواسطة',
    total: 'الإجمالي',
    itemsCount: 'عدد العناصر',
    filterBranch: 'تصفية حسب الفرع',
    clearFilters: 'مسح الفلاتر',
    export: 'تصدير',
    refresh: 'تحديث',
    loading: 'جاري التحميل...',
    summary: 'ملخص',
    item: 'عنصر',
    available: 'المتاح',
    requiredQuantity: 'الكمية المطلوبة',
    approvedQuantity: 'الكمية المقبولة',
    pendingQuantity: 'الكمية المعلقة',
    originalQuantity: 'الكمية الأصلية',
    approveItem: 'قبول العنصر',
    rejectItem: 'رفض العنصر',
    resetItem: 'إعادة العنصر',
    confirmApproval: 'موافقة',
    confirmRejection: 'رفض',
    partialApproval: 'موافقة جزئية',
    approveModalTitle: 'مراجعة المرتجع: {returnNumber}',
    approvedItems: 'العناصر المقبولة',
    rejectedItems: 'العناصر المرفوضة',
    partiallyApproved: 'العناصر المقبولة جزئياً',
    showMore: 'عرض المزيد',
    showLess: 'عرض أقل',
    attentionRequired: 'يتطلب انتباهك',
    pagination: {
      previous: 'السابق',
      next: 'التالي',
      page: 'صفحة {current} من {total}',
      showing: 'عرض {from} إلى {to} من {total} نتيجة',
    },
    errors: {
      noBranch: 'لم يتم العثور على فرع',
      fetchReturns: 'خطأ في جلب المرتجعات',
      fetchBranches: 'خطأ في جلب الفروع',
      createReturn: 'خطأ في إنشاء المرتجع',
      updateReturn: 'خطأ في تحديث حالة المرتجع',
      invalidForm: 'البيانات المدخلة غير صالحة',
      required: 'حقل {field} مطلوب',
      invalidQuantityMin: 'الحد الأدنى للكمية هو {min}',
      invalidQuantityMax: 'الكمية يجب أن تكون بين {min} و{max}',
      noItemSelected: 'لم يتم اختيار عنصر',
      insufficientQuantity: 'الكمية المحجوزة غير كافية. هل تريد المتابعة بالموافقة على الكميات المتاحة فقط؟',
      productNotFound: 'المنتج غير موجود',
      invalidReasonPair: 'سبب الإرجاع غير متطابق',
      writeConflict: 'تعارض في الكتابة، حاول مرة أخرى',
      accessDenied: 'غير مصرح لك بالوصول إلى هذه الصفحة',
      networkError: 'خطأ في الاتصال بالخادم',
      invalidQuantity: 'الكمية المدخلة غير صالحة',
      approvalRequired: 'يجب الموافقة على عنصر واحد على الأقل',
    },
    notifications: {
      return_created: 'تم إنشاء مرتجع جديد: {returnNumber} من {branchName}',
      return_status_updated: 'تم تحديث حالة المرتجع {returnNumber} إلى {status}',
    },
    common: {
      cancel: 'إلغاء',
      close: 'إغلاق',
      retry: 'إعادة المحاولة',
      submitting: 'جاري الإرسال...',
      save: 'حفظ',
      delete: 'حذف',
      edit: 'تعديل',
      confirm: 'تأكيد',
      yes: 'نعم',
      no: 'لا',
      increase: 'زيادة',
      decrease: 'تقليل',
      selectAll: 'تحديد الكل',
      deselectAll: 'إلغاء تحديد الكل',
    },
    validation: {
      quantityStep: 'الكمية يجب أن تكون من مضاعفات 0.25',
      quantityInteger: 'الكمية يجب أن تكون رقماً صحيحاً',
      minQuantity: 'الكمية يجب أن تكون 0.25 على الأقل',
    },
    
    // إضافات جديدة
    confirmPartialApproval: 'تأكيد الموافقة الجزئية',
    partialApprovalOptions: 'خيارات الموافقة:',
    useAvailableQuantity: 'استخدام الكميات المتاحة فقط',
    useAvailableQuantityDesc: 'سيتم الموافقة على الكميات المتاحة فقط، والباقي سيرفض تلقائياً',
    forceApproveAll: 'الموافقة بالقوة على كل الكميات',
    forceApproveAllDesc: 'سيتم الموافقة على كل الكميات حتى لو لم تكن متاحة (قد يؤثر على دقة المخزون)',
    note: 'ملاحظة',
    partialApprovalNote: 'يجب مراجعة المخزون بعد الموافقة للتأكد من صحته',
    confirmWithAvailable: 'تأكيد بالموافقة على المتاح',
    confirmForceApprove: 'تأكيد الموافقة بالقوة',
    success: {
      partialApproval: 'تمت الموافقة على الكميات المتاحة بنجاح',
      forceApproval: 'تمت الموافقة بالقوة بنجاح. يرجى مراجعة المخزون.',
    },
    
    // إضافات جديدة للزيادة
    allowQuantityIncrease: 'السماح بزيادة الكمية',
    allowQuantityIncreaseDesc: 'تمكين الزيادة في الكميات المقبولة عن الكمية الأصلية',
    quantityIncreased: 'تمت الزيادة',
    approveWithIncrease: 'موافقة مع زيادة',
  },
  en: {
    title: 'Returns Management',
    subtitle: 'Create, track, and manage return requests',
    noReturns: 'No returns found',
    returnNumber: 'Return Number',
    statusLabel: 'Status',
    date: 'Date',
    totalQuantity: 'Total Quantity',
    totalPrice: 'Total Price',
    branch: 'Branch',
    selectBranch: 'Select Branch',
    notesLabel: 'Notes',
    reviewNotes: 'Review Notes',
    noNotes: 'No notes available',
    createReturn: 'Create New Return',
    viewReturn: 'View Return',
    approveReturn: 'Approve Return',
    rejectReturn: 'Reject Return',
    view: 'View',
    searchPlaceholder: 'Search by return number or product...',
    filterStatus: 'Filter by Status',
    allStatuses: 'All Statuses',
    selectProduct: 'Select Product',
    reason: 'Return Reason',
    selectReason: 'Select Reason',
    damaged: 'Damaged',
    wrongItem: 'Wrong Item',
    excessQuantity: 'Excess Quantity',
    other: 'Other',
    notesPlaceholder: 'Additional notes (optional)',
    reviewNotesPlaceholder: 'Review notes (optional)',
    items: 'Items',
    addItem: 'Add Item',
    removeItem: 'Remove Item',
    submitReturn: 'Submit Return',
    approve: 'Approve',
    reject: 'Reject',
    approveAll: 'Approve All',
    rejectAll: 'Reject All',
    increaseAll: 'Increase All',
    partialApprove: 'Partial Approve',
    createSuccess: 'Return created successfully',
    updateSuccess: 'Return status updated successfully',
    newReturnNotification: 'New return created: {returnNumber}',
    quantity: 'Quantity',
    unit: 'Unit',
    department: 'Department',
    product: 'Product',
    price: 'Price',
    currency: 'SAR',
    status: {
      pending: 'Pending Review',
      approved: 'Approved',
      rejected: 'Rejected',
      all: 'All Statuses',
    },
    actions: 'Actions',
    createdAt: 'Created At',
    createdBy: 'Created By',
    total: 'Total',
    itemsCount: 'Items Count',
    filterBranch: 'Filter by Branch',
    clearFilters: 'Clear Filters',
    export: 'Export',
    refresh: 'Refresh',
    loading: 'Loading...',
    summary: 'Summary',
    item: 'Item',
    available: 'Available',
    requiredQuantity: 'Required Quantity',
    approvedQuantity: 'Approved Quantity',
    pendingQuantity: 'Pending Quantity',
    originalQuantity: 'Original Quantity',
    approveItem: 'Approve Item',
    rejectItem: 'Reject Item',
    resetItem: 'Reset Item',
    confirmApproval: 'Approve',
    confirmRejection: 'Reject',
    partialApproval: 'Partial Approval',
    approveModalTitle: 'Review Return: {returnNumber}',
    approvedItems: 'Approved Items',
    rejectedItems: 'Rejected Items',
    partiallyApproved: 'Partially Approved Items',
    showMore: 'Show More',
    showLess: 'Show Less',
    attentionRequired: 'Requires your attention',
    pagination: {
      previous: 'Previous',
      next: 'Next',
      page: 'Page {current} of {total}',
      showing: 'Showing {from} to {to} of {total} results',
    },
    errors: {
      noBranch: 'No branch found',
      fetchReturns: 'Error fetching returns',
      fetchBranches: 'Error fetching branches',
      createReturn: 'Error creating return',
      updateReturn: 'Error updating return status',
      invalidForm: 'Invalid form data',
      required: '{field} is required',
      invalidQuantityMin: 'Minimum quantity is {min}',
      invalidQuantityMax: 'Quantity must be between {min} and {max}',
      noItemSelected: 'No item selected',
      insufficientQuantity: 'Reserved quantities are insufficient. Do you want to proceed with approving only available quantities?',
      productNotFound: 'Product not found',
      invalidReasonPair: 'Return reason mismatch',
      writeConflict: 'Write conflict, please try again',
      accessDenied: 'You are not authorized to access this page',
      networkError: 'Network connection error',
      invalidQuantity: 'Invalid quantity entered',
      approvalRequired: 'At least one item must be approved',
    },
    notifications: {
      return_created: 'New return created: {returnNumber} from {branchName}',
      return_status_updated: 'Return {returnNumber} status updated to {status}',
    },
    common: {
      cancel: 'Cancel',
      close: 'Close',
      retry: 'Retry',
      submitting: 'Submitting...',
      save: 'Save',
      delete: 'Delete',
      edit: 'Edit',
      confirm: 'Confirm',
      yes: 'Yes',
      no: 'No',
      increase: 'Increase',
      decrease: 'Decrease',
      selectAll: 'Select All',
      deselectAll: 'Deselect All',
    },
    validation: {
      quantityStep: 'Quantity must be in multiples of 0.25',
      quantityInteger: 'Quantity must be a whole number',
      minQuantity: 'Quantity must be at least 0.25',
    },
    
    // New additions
    confirmPartialApproval: 'Confirm Partial Approval',
    partialApprovalOptions: 'Approval Options:',
    useAvailableQuantity: 'Use Available Quantities Only',
    useAvailableQuantityDesc: 'Only available quantities will be approved, the rest will be automatically rejected',
    forceApproveAll: 'Force Approve All Quantities',
    forceApproveAllDesc: 'All quantities will be approved even if not available (may affect inventory accuracy)',
    note: 'Note',
    partialApprovalNote: 'Please review inventory after approval to ensure accuracy',
    confirmWithAvailable: 'Confirm with Available',
    confirmForceApprove: 'Confirm Force Approval',
    success: {
      partialApproval: 'Available quantities approved successfully',
      forceApproval: 'Force approved successfully. Please review inventory.',
    },
    
    // New additions for quantity increase
    allowQuantityIncrease: 'Allow Quantity Increase',
    allowQuantityIncreaseDesc: 'Enable increasing approved quantities beyond original amount',
    quantityIncreased: 'Increased',
    approveWithIncrease: 'Approve with Increase',
  },
};

export const BranchReturns: React.FC = () => {
  const { language } = useLanguage();
  const isRtl = language === 'ar';
  const t = translations[isRtl ? 'ar' : 'en'];
  const { user } = useAuth();
  const { socket, isConnected } = useSocket();
  const { addNotification } = useNotifications();
  const queryClient = useQueryClient();
  
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState<ReturnStatus | ''>('');
  const [filterBranch, setFilterBranch] = useState<string>('');
  const [currentPage, setCurrentPage] = useState(1);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isApproveModalOpen, setIsApproveModalOpen] = useState(false);
  const [selectedReturn, setSelectedReturn] = useState<Return | null>(null);
  const [returnForm, dispatchReturnForm] = useReducer(returnFormReducer, { notes: '', items: [] });
  const [returnErrors, setReturnErrors] = useState<Record<string, string>>({});
  const [availableItems, setAvailableItems] = useState<AvailableItem[]>([]);
  const [reviewNotes, setReviewNotes] = useState('');
  const [expandedReturnId, setExpandedReturnId] = useState<string | null>(null);
  
  // حالات جديدة للتعامل مع الموافقة بالقوة
  const [showForceApproveModal, setShowForceApproveModal] = useState(false);
  const [approvalError, setApprovalError] = useState<string | null>(null);
  const [pendingApproval, setPendingApproval] = useState<{
    approvedItems: ItemApproval[];
    reviewNotes: string;
    status: ReturnStatus;
  } | null>(null);
  
  const RETURNS_PER_PAGE = 9;
  const INITIAL_VISIBLE_ITEMS = 3;

  if (user?.role === 'chef') {
    return (
      <div className={`p-4 md:p-6 ${isRtl ? 'text-right' : 'text-left'}`}>
        <div className="p-6 bg-red-50 rounded-xl shadow-sm border border-red-200">
          <p className="text-red-600 text-sm font-medium">{t.errors.accessDenied}</p>
        </div>
      </div>
    );
  }

  const { data: branchesData } = useQuery<Branch[], Error>({
    queryKey: ['branches', language],
    queryFn: async () => {
      const response = await returnsAPI.getBranches();
      return response.branches.map((branch: any) => ({
        _id: branch._id,
        name: branch.name,
        nameEn: branch.nameEn || branch.name,
        displayName: isRtl ? branch.name : branch.nameEn || branch.name,
      }));
    },
    enabled: user?.role === 'admin' || user?.role === 'production',
    staleTime: 10 * 60 * 1000,
  });

  const { data: returnsData, isLoading: returnsLoading, refetch: refetchReturns } = useQuery<
    { returns: Return[]; total: number; totalPages: number },
    Error
  >({
    queryKey: ['returns', user?.branchId, filterStatus, filterBranch, searchQuery, currentPage, language],
    queryFn: async () => {
      const query: any = {
        status: filterStatus || undefined,
        search: searchQuery || undefined,
        page: currentPage,
        limit: RETURNS_PER_PAGE,
      };
      
      if (user?.role === 'branch' && user.branchId) {
        query.branch = user.branchId;
      } else if (filterBranch) {
        query.branch = filterBranch;
      }
      
      const response = await returnsAPI.getAll(query);
      return {
        returns: response.returns.map((ret: any) => ({
          _id: ret._id,
          returnNumber: ret.returnNumber || `RET-${ret._id.slice(-6)}`,
          branch: ret.branch ? {
            _id: ret.branch._id,
            name: ret.branch.name,
            nameEn: ret.branch.nameEn || ret.branch.name,
            displayName: isRtl ? ret.branch.name : ret.branch.nameEn || ret.branch.name,
          } : null,
          items: Array.isArray(ret.items) ? ret.items.map((item: any) => ({
            itemId: item.itemId || item._id,
            product: {
              _id: item.product?._id || '',
              name: item.product?.name || t.product,
              nameEn: item.product?.nameEn || item.product?.name || t.product,
              unit: item.product?.unit || t.unit,
              unitEn: item.product?.unitEn || item.product?.unit || t.unit,
              displayName: isRtl ? item.product?.name : item.product?.nameEn || item.product?.name || t.product,
              displayUnit: isRtl ? item.product?.unit : item.product?.unitEn || item.product?.unit || t.unit,
              department: item.product?.department ? {
                name: item.product.department.name,
                nameEn: item.product.department.nameEn || item.product.department.name,
                displayName: isRtl ? item.product.department.name : item.product.department.nameEn || item.product.department.name,
              } : null,
              price: item.product?.price || 0,
            },
            quantity: item.quantity || 1,
            reason: item.reason || '',
            reasonEn: item.reasonEn || '',
            approvedQuantity: item.approvedQuantity || 0,
            isRejected: item.isRejected || false
          })) : [],
          status: ret.status || ReturnStatus.PENDING,
          createdAt: ret.createdAt || new Date().toISOString(),
          notes: ret.notes || '',
          reviewNotes: ret.reviewNotes || '',
          createdBy: ret.createdBy || {},
          reviewedBy: ret.reviewedBy || {},
        })),
        total: response.total || 0,
        totalPages: response.totalPages || 1,
      };
    },
    enabled: !!user && user.role !== 'chef',
    staleTime: 5 * 60 * 1000,
    onError: (err) => {
      toast.error(err.message || t.errors.fetchReturns, {
        position: isRtl ? 'top-right' : 'top-left',
        toastId: `fetch-returns-${Date.now()}`,
      });
    },
  });

  const { data: inventoryData } = useQuery<AvailableItem[], Error>({
    queryKey: ['inventory', user?.branchId, language],
    queryFn: async () => {
      if (!user?.branchId) return [];
      try {
        const response = await inventoryAPI.getByBranch(user.branchId);
        return Array.isArray(response) ? response
          .filter((item: any) => item.currentStock > 0 && item.product)
          .map((item: any) => ({
            productId: item.product._id,
            productName: isRtl ? item.product.name : item.product.nameEn || item.product.name,
            available: item.currentStock,
            unit: item.product.unit || t.unit,
            displayUnit: isRtl ? item.product.unit : item.product.unitEn || item.product.unit || t.unit,
            departmentName: item.product.department
              ? (isRtl ? item.product.department.name : item.product.department.nameEn || item.product.department.name)
              : t.department,
            stock: item.currentStock,
            price: item.product.price || 0,
          })) : [];
      } catch (error) {
        console.error('Error fetching inventory:', error);
        return [];
      }
    },
    enabled: !!user?.branchId && user?.role === 'branch',
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (!socket || !user || !isConnected) return;

    socket.emit('joinRoom', {
      role: user.role,
      branchId: user.branchId,
      userId: user._id,
    });

    const handleReturnCreated = (data: {
      branchId: string;
      returnId: string;
      returnNumber: string;
      branchName?: string;
      eventId: string;
    }) => {
      if (user.role === 'branch' && data.branchId !== user.branchId) return;
      queryClient.invalidateQueries({ queryKey: ['returns'] });
      addNotification({
        _id: data.eventId,
        type: 'success',
        message: t.notifications.return_created
          .replace('{returnNumber}', data.returnNumber)
          .replace('{branchName}', data.branchName || t.branch),
        data: { returnId: data.returnId, branchId: data.branchId, eventId: data.eventId },
        read: false,
        createdAt: new Date().toISOString(),
      });
    };

    const handleReturnStatusUpdated = (data: {
      branchId: string;
      returnId: string;
      status: string;
      returnNumber: string;
      branchName?: string;
      eventId: string;
    }) => {
      if (user.role === 'branch' && data.branchId !== user.branchId) return;
      queryClient.invalidateQueries({ queryKey: ['returns'] });
      addNotification({
        _id: data.eventId,
        type: 'info',
        message: t.notifications.return_status_updated
          .replace('{returnNumber}', data.returnNumber)
          .replace('{status}', t.status[data.status as keyof typeof t.status] || data.status)
          .replace('{branchName}', data.branchName || t.branch),
        data: { returnId: data.returnId, branchId: data.branchId, eventId: data.eventId },
        read: false,
        createdAt: new Date().toISOString(),
      });
    };

    socket.on('connect', () => {
      console.log('Socket connected for returns');
    });

    socket.on('returnCreated', handleReturnCreated);
    socket.on('returnStatusUpdated', handleReturnStatusUpdated);

    return () => {
      socket.off('connect');
      socket.off('returnCreated', handleReturnCreated);
      socket.off('returnStatusUpdated', handleReturnStatusUpdated);
    };
  }, [socket, user, isConnected, queryClient, t, addNotification]);

  useEffect(() => {
    if (inventoryData) {
      setAvailableItems(inventoryData);
    }
  }, [inventoryData]);

  const branchOptions = useMemo(() => [
    { value: '', label: t.selectBranch },
    ...(branchesData || []).map((branch) => ({
      value: branch._id,
      label: branch.displayName,
    })),
  ], [branchesData, t]);

  const statusOptions = useMemo(() => [
    { value: '', label: t.status.all },
    { value: ReturnStatus.PENDING, label: t.status.pending },
    { value: ReturnStatus.APPROVED, label: t.status.approved },
    { value: ReturnStatus.REJECTED, label: t.status.rejected },
  ], [t]);

  const reasonOptions = useMemo(() => [
    { value: '', label: t.selectReason, enValue: '' },
    { value: ReturnReason.DAMAGED_AR, label: t.damaged, enValue: ReturnReason.DAMAGED_EN },
    { value: ReturnReason.WRONG_ITEM_AR, label: t.wrongItem, enValue: ReturnReason.WRONG_ITEM_EN },
    { value: ReturnReason.EXCESS_QUANTITY_AR, label: t.excessQuantity, enValue: ReturnReason.EXCESS_QUANTITY_EN },
    { value: ReturnReason.OTHER_AR, label: t.other, enValue: ReturnReason.OTHER_EN },
  ], [t]);

  const productOptions = useMemo(() => [
    { value: '', label: t.selectProduct },
    ...availableItems.map((item) => ({
      value: item.productId,
      label: `${item.productName} (${item.available} ${item.displayUnit})`,
      data: item,
    })),
  ], [availableItems, t]);

  const handleSearch = useCallback((value: string) => {
    setSearchQuery(value);
    setCurrentPage(1);
  }, []);

  const handleClearFilters = useCallback(() => {
    setSearchQuery('');
    setFilterStatus('');
    setFilterBranch('');
    setCurrentPage(1);
  }, []);

  const totalReturns = returnsData?.total || 0;
  const totalPages = returnsData?.totalPages || 1;
  const startIndex = (currentPage - 1) * RETURNS_PER_PAGE + 1;
  const endIndex = Math.min(currentPage * RETURNS_PER_PAGE, totalReturns);

  const addItemToForm = useCallback(() => {
    dispatchReturnForm({
      type: 'ADD_ITEM',
      payload: { 
        productId: '', 
        quantity: 1,
        reason: '', 
        reasonEn: '', 
        maxQuantity: 0 
      },
    });
  }, []);

  const updateItemInForm = useCallback((index: number, field: keyof ReturnItem, value: any) => {
    if (field === 'productId') {
      const product = availableItems.find(item => item.productId === value);
      dispatchReturnForm({
        type: 'UPDATE_ITEM',
        payload: { index, field: 'productId', value },
      });
      if (product) {
        dispatchReturnForm({
          type: 'UPDATE_ITEM',
          payload: { index, field: 'maxQuantity', value: product.available },
        });
        
        const initialQuantity = /كيلو|كجم|kg|kilo/i.test(product.unit) ? 0.1 : 1;
        dispatchReturnForm({
          type: 'UPDATE_ITEM',
          payload: { index, field: 'quantity', value: initialQuantity },
        });
      }
    } else if (field === 'reason') {
      const selectedReason = reasonOptions.find(opt => opt.value === value);
      dispatchReturnForm({
        type: 'UPDATE_ITEM',
        payload: { index, field: 'reason', value },
      });
      dispatchReturnForm({
        type: 'UPDATE_ITEM',
        payload: { index, field: 'reasonEn', value: selectedReason?.enValue || '' },
      });
    } else if (field === 'quantity') {
      let numValue = typeof value === 'string' ? parseFloat(value) : value;
      if (isNaN(numValue)) numValue = 1;
      
      const item = returnForm.items[index];
      const product = availableItems.find(p => p.productId === item.productId);
      const isKg = product && /كيلو|كجم|kg|kilo/i.test(product.unit);
      const minValue = isKg ? 0.1 : 1;
      
      // تقريب إلى منزلة عشرية واحدة
      numValue = parseFloat(numValue.toFixed(1));
      
      // التحقق من الحد الأدنى
      if (numValue < minValue) {
        toast.error(t.errors.invalidQuantityMin.replace('{min}', minValue.toString()));
        numValue = minValue;
      }
      
      // التحقق من الحد الأقصى
      if (item && item.maxQuantity && numValue > item.maxQuantity) {
        toast.error(t.errors.invalidQuantityMax.replace('{min}', minValue.toString()).replace('{max}', item.maxQuantity.toString()));
        numValue = item.maxQuantity;
      }
      
      dispatchReturnForm({
        type: 'UPDATE_ITEM',
        payload: { index, field: 'quantity', value: numValue },
      });
    } else {
      dispatchReturnForm({ type: 'UPDATE_ITEM', payload: { index, field, value } });
    }
  }, [availableItems, reasonOptions, returnForm.items, t]);

  const removeItemFromForm = useCallback((index: number) => {
    dispatchReturnForm({ type: 'REMOVE_ITEM', payload: index });
  }, []);

  const validateReturnForm = useCallback(() => {
    const errors: Record<string, string> = {};
    
    if (!user?.branchId && user?.role === 'branch') {
      errors.form = t.errors.noBranch;
    }
    
    if (returnForm.items.length === 0) {
      errors.items = t.errors.required.replace('{field}', t.items);
    }
    
    returnForm.items.forEach((item, index) => {
      if (!item.productId) {
        errors[`item_${index}_productId`] = t.errors.required.replace('{field}', t.product);
      }
      
      if (!item.reason) {
        errors[`item_${index}_reason`] = t.errors.required.replace('{field}', t.reason);
      }
      
      const product = availableItems.find(p => p.productId === item.productId);
      const isKg = product && /كيلو|كجم|kg|kilo/i.test(product.unit);
      const minValue = isKg ? 0.1 : 1;
      
      if (item.quantity < minValue) {
        errors[`item_${index}_quantity`] = t.errors.invalidQuantityMin.replace('{min}', minValue.toString());
      } else if (item.maxQuantity && item.quantity > item.maxQuantity) {
        errors[`item_${index}_quantity`] = t.errors.invalidQuantityMax
          .replace('{min}', minValue.toString())
          .replace('{max}', item.maxQuantity.toString());
      }
    });
    
    setReturnErrors(errors);
    return Object.keys(errors).length === 0;
  }, [returnForm, t, user, availableItems]);

  const createReturnMutation = useMutation({
    mutationFn: async () => {
      if (!validateReturnForm()) throw new Error(t.errors.invalidForm);
      if (!user?.branchId) throw new Error(t.errors.noBranch);
      
      const data = {
        branchId: user.branchId,
        items: returnForm.items.map((item) => ({
          product: item.productId,
          quantity: item.quantity,
          reason: item.reason,
          reasonEn: item.reasonEn,
        })),
        notes: returnForm.notes || undefined,
      };
      
      return await returnsAPI.createReturn(data);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['returns'] });
      setIsCreateModalOpen(false);
      dispatchReturnForm({ type: 'RESET' });
      setReturnErrors({});
      
      toast.success(t.createSuccess, {
        position: isRtl ? 'top-right' : 'top-left',
      });
      
      if (isConnected && socket) {
        socket.emit('returnCreated', {
          branchId: user?.branchId,
          returnId: data._id,
          returnNumber: data.returnNumber,
          branchName: user?.branch?.displayName,
          eventId: crypto.randomUUID(),
        });
      }
    },
    onError: (err: Error) => {
      toast.error(err.message || t.errors.createReturn, {
        position: isRtl ? 'top-right' : 'top-left',
      });
    },
  });

  const updateReturnStatusMutation = useMutation({
    mutationFn: async ({ 
      returnId, 
      status, 
      reviewNotes,
      approvedItems,
      forceApprove = false
    }: { 
      returnId: string; 
      status: ReturnStatus; 
      reviewNotes?: string;
      approvedItems?: ItemApproval[];
      forceApprove?: boolean;
    }) => {
      return await returnsAPI.updateReturnStatus(returnId, status, reviewNotes, approvedItems, forceApprove);
    },
    onSuccess: (data, { status, forceApprove }) => {
      queryClient.invalidateQueries({ queryKey: ['returns'] });
      setIsApproveModalOpen(false);
      setShowForceApproveModal(false);
      setReviewNotes('');
      setSelectedReturn(null);
      setApprovalError(null);
      setPendingApproval(null);
      
      if (forceApprove) {
        toast.success(t.success.forceApproval, {
          position: isRtl ? 'top-right' : 'top-left',
        });
      } else {
        toast.success(t.updateSuccess, {
          position: isRtl ? 'top-right' : 'top-left',
        });
      }
      
      if (isConnected && socket && selectedReturn) {
        socket.emit('returnStatusUpdated', {
          branchId: selectedReturn.branch?._id,
          returnId,
          status,
          returnNumber: selectedReturn.returnNumber,
          branchName: selectedReturn.branch?.displayName,
          eventId: crypto.randomUUID(),
        });
      }
    },
    onError: (err: any) => {
      // لا نعرض toast هنا، سنتعامل مع الخطأ في handleApproveSubmit
      throw err;
    },
  });

  const handleApproveSubmit = async (
    approvedItems: ItemApproval[], 
    reviewNotes: string, 
    status: ReturnStatus,
    forceApprove?: boolean
  ) => {
    if (!selectedReturn) return;
    
    try {
      await updateReturnStatusMutation.mutateAsync({
        returnId: selectedReturn._id,
        status,
        reviewNotes,
        approvedItems,
        forceApprove
      });
      
    } catch (err: any) {
      console.error('Approval error:', err);
      
      // إذا كان الخطأ بسبب الكمية غير كافية
      if (err.status === 422 && (err.message?.includes('غير كافية') || err.message?.includes('Insufficient'))) {
        // حفظ محاولة الموافقة
        setPendingApproval({
          approvedItems,
          reviewNotes,
          status
        });
        
        // حفظ رسالة الخطأ
        setApprovalError(err.message || err.data?.error);
        
        // عرض مودال التأكيد
        setShowForceApproveModal(true);
        
        // إخفاء مودال الموافقة
        setIsApproveModalOpen(false);
      } else {
        // إعادة عرض الخطأ العادي
        toast.error(err.message || t.errors.updateReturn, {
          position: isRtl ? 'top-right' : 'top-left',
        });
      }
    }
  };

  const toggleReturnExpansion = (returnId: string) => {
    setExpandedReturnId(prev => prev === returnId ? null : returnId);
  };

  const formatDateWithoutSeconds = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString(isRtl ? 'ar-EG' : 'en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const ReturnCard = useCallback(({ ret }: { ret: Return }) => {
    const statusInfo = {
      [ReturnStatus.PENDING]: { 
        color: 'bg-amber-50 text-amber-700 border-amber-200', 
        icon: Clock, 
        label: t.status.pending 
      },
      [ReturnStatus.APPROVED]: { 
        color: 'bg-green-50 text-green-700 border-green-200', 
        icon: Check, 
        label: t.status.approved 
      },
      [ReturnStatus.REJECTED]: { 
        color: 'bg-red-50 text-red-700 border-red-200', 
        icon: XCircle, 
        label: t.status.rejected 
      },
    }[ret.status];
    
    const totalQuantity = ret.items.reduce((sum, item) => sum + item.quantity, 0);
    const totalApprovedQuantity = ret.items.reduce((sum, item) => sum + (item.approvedQuantity || 0), 0);
    const isExpanded = expandedReturnId === ret._id;
    const hasIncreasedItems = ret.items.some(item => (item.approvedQuantity || 0) > item.quantity);

    return (
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-all duration-300">
        <div className="p-5">
          {/* الصف العلوي: الرقم والحالة */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-50 rounded-lg">
                <Package className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  #{ret.returnNumber}
                </h3>
                <div className="flex items-center gap-2 mt-1">
                  <span className={`text-xs px-2.5 py-1 rounded-full ${statusInfo.color} border flex items-center gap-1.5`}>
                    <statusInfo.icon className="w-3 h-3" />
                    {statusInfo.label}
                  </span>
                  {hasIncreasedItems && ret.status === ReturnStatus.APPROVED && (
                    <span className="text-xs px-2.5 py-1 rounded-full bg-purple-50 text-purple-700 border border-purple-200 flex items-center gap-1.5">
                      <Layers className="w-3 h-3" />
                      {t.quantityIncreased}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* الصف الثاني: التاريخ والفرع */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-5 p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-2 text-sm">
              <Calendar className="w-4 h-4 text-gray-400" />
              <span className="text-gray-600">{formatDateWithoutSeconds(ret.createdAt)}</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Box className="w-4 h-4 text-gray-400" />
              <span className="text-gray-600 font-medium">{ret.branch?.displayName || t.branch}</span>
            </div>
          </div>

          {/* إحصائيات سريعة */}
          <div className="grid grid-cols-2 gap-3 mb-5">
            <div className="bg-gray-50 p-3 rounded-lg">
              <p className="text-xs text-gray-500 mb-1">{t.itemsCount}</p>
              <p className="text-lg font-semibold text-gray-900">{ret.items.length}</p>
            </div>
            <div className="bg-gray-50 p-3 rounded-lg">
              <p className="text-xs text-gray-500 mb-1">{t.approvedQuantity}</p>
              <p className="text-lg font-semibold text-gray-900">{totalApprovedQuantity.toFixed(1)}</p>
            </div>
          </div>

          {/* العناصر */}
          <div className="mb-4">
            <p className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
              <Archive className="w-4 h-4 text-gray-500" />
              {t.items}:
            </p>
            <div className="space-y-2">
              {ret.items.slice(0, isExpanded ? ret.items.length : 3).map((item, idx) => {
                const approvedQty = item.approvedQuantity || 0;
                const isIncreased = approvedQty > item.quantity;
                
                return (
                  <div key={idx} className="flex items-center justify-between text-sm p-2.5 hover:bg-gray-50 rounded-lg transition-colors">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-gray-800 truncate block font-medium">{item.product.displayName}</span>
                        {isIncreased && (
                          <span className="text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded">
                            +{(approvedQty - item.quantity).toFixed(1)}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-xs text-gray-500 mt-0.5">
                        <span>{item.quantity} → {approvedQty} {item.product.displayUnit}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
              {ret.items.length > 3 && (
                <button
                  onClick={() => toggleReturnExpansion(ret._id)}
                  className="text-sm text-amber-600 hover:text-amber-700 font-medium flex items-center gap-1.5 mt-2 transition-colors"
                >
                  {isExpanded ? (
                    <>
                      <ChevronUp className="w-4 h-4" />
                      {t.showLess}
                    </>
                  ) : (
                    <>
                      <ChevronDown className="w-4 h-4" />
                      {t.showMore} ({ret.items.length - 3})
                    </>
                  )}
                </button>
              )}
            </div>
          </div>

          {/* الملاحظات */}
          {ret.notes && (
            <div className="mb-4 p-3 bg-amber-50 rounded-lg border border-amber-100">
              <div className="flex items-center gap-2">
                <Info className="w-4 h-4 text-amber-600" />
                <p className="text-xs text-amber-700 truncate">{ret.notes}</p>
              </div>
            </div>
          )}

          {/* الإجراءات */}
          <div className="flex flex-wrap flex-row-reverse gap-2 pt-4 border-t border-gray-200">
            <button
              onClick={() => {
                setSelectedReturn(ret);
                setIsViewModalOpen(true);
              }}
              className="px-3.5 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-xs font-medium transition-all duration-200 flex items-center gap-1.5 active:scale-95"
            >
              <Eye className="w-4 h-4" />
              {t.view}
            </button>
            
            {['admin', 'production'].includes(user?.role || '') && ret.status === ReturnStatus.PENDING && (
              <button
                onClick={() => {
                  setSelectedReturn(ret);
                  setIsApproveModalOpen(true);
                }}
                className="px-3.5 py-2 bg-green-100 hover:bg-green-200 text-green-700 rounded-lg text-xs font-medium transition-all duration-200 flex items-center gap-1.5 active:scale-95"
              >
                <ShieldCheck className="w-4 h-4" />
                {t.approveReturn}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }, [t, user, expandedReturnId, formatDateWithoutSeconds]);

  return (
    <div className={`p-4 md:p-6 min-h-screen ${isRtl ? 'text-right' : 'text-left'}`}>
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
              <Package className="w-6 h-6 text-amber-600" />
              {t.title}
            </h1>
            <p className="text-sm text-gray-600 mt-1">{t.subtitle}</p>
          </div>
          
          {user?.role === 'branch' && (
            <button
              onClick={() => setIsCreateModalOpen(true)}
              className="px-4 py-2.5 bg-amber-600 hover:bg-amber-700 text-white rounded-lg font-medium transition-all duration-200 flex items-center gap-2 active:scale-95"
            >
              <Plus className="w-5 h-5" />
              {t.createReturn}
            </button>
          )}
        </div>
      </div>

      {/* فلترة البحث - تصميم متجاوب */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
        {/* للشاشات الكبيرة: سيرش + فلترين في صف واحد */}
        <div className="hidden lg:block">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* البحث يأخذ نصف الصف */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                placeholder={t.searchPlaceholder}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
              />
            </div>

            {/* الفلاتر تأخذ النصف الثاني من الصف */}
            <div className="grid grid-cols-2 gap-4">
              <CustomDropdown
                value={filterStatus}
                onChange={(value) => {
                  setFilterStatus(value as ReturnStatus | '');
                  setCurrentPage(1);
                }}
                options={statusOptions}
                placeholder={t.filterStatus}
                icon={<Filter className="w-4 h-4 text-gray-400" />}
              />
              
              {(user?.role === 'admin' || user?.role === 'production') && (
                <CustomDropdown
                  value={filterBranch}
                  onChange={(value) => {
                    setFilterBranch(value);
                    setCurrentPage(1);
                  }}
                  options={branchOptions}
                  placeholder={t.filterBranch}
                />
              )}
            </div>
          </div>
        </div>

        {/* للشاشات الصغيرة: البحث في صف والفلاتر في صف */}
        <div className="lg:hidden space-y-4">
          {/* صف البحث */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              placeholder={t.searchPlaceholder}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
            />
          </div>

          {/* صف الفلاتر */}
          <div className="grid grid-cols-2 gap-4">
            <CustomDropdown
              value={filterStatus}
              onChange={(value) => {
                setFilterStatus(value as ReturnStatus | '');
                setCurrentPage(1);
              }}
              options={statusOptions}
              placeholder={t.filterStatus}
              icon={<Filter className="w-4 h-4 text-gray-400" />}
            />
            
            {(user?.role === 'admin' || user?.role === 'production') && (
              <CustomDropdown
                value={filterBranch}
                onChange={(value) => {
                  setFilterBranch(value);
                  setCurrentPage(1);
                }}
                options={branchOptions}
                placeholder={t.filterBranch}
              />
            )}
          </div>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mt-4 pt-4 border-t border-gray-200">
          <div className="flex items-center gap-2">
            {(searchQuery || filterStatus || filterBranch) && (
              <button
                onClick={handleClearFilters}
                className="px-3 py-1.5 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md transition-all duration-200 active:scale-95"
              >
                {t.clearFilters}
              </button>
            )}
            <span className="text-xs text-gray-500">
              {t.pagination.showing
                .replace('{from}', startIndex.toString())
                .replace('{to}', endIndex.toString())
                .replace('{total}', totalReturns.toString())}
            </span>
          </div>
          
          <div className="flex items-center gap-2">
            <button
              onClick={() => refetchReturns()}
              className="p-2 text-gray-600 hover:text-amber-600 transition-all duration-200 active:scale-95"
              title={t.refresh}
            >
              <RefreshCw className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* قائمة المرتجعات */}
      {returnsLoading ? (
        <div className="grid grid-cols-1 gap-4">
          {[...Array(9)].map((_, i) => (
            <div key={i} className="bg-white rounded-lg border border-gray-200 p-4 animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-3/4 mb-3"></div>
              <div className="h-3 bg-gray-200 rounded w-1/2 mb-2"></div>
              <div className="h-3 bg-gray-200 rounded w-1/3"></div>
            </div>
          ))}
        </div>
      ) : returnsData?.returns.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
          <Package className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 text-sm">{t.noReturns}</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4">
            {returnsData?.returns.map((ret) => (
              <ReturnCard key={ret._id} ret={ret} />
            ))}
          </div>

          {totalPages > 1 && (
            <div className={`mt-6 flex flex-col sm:flex-row items-center justify-between gap-4 ${isRtl ? 'sm:flex-row' : ''}`}>
              <div className="text-sm text-gray-600">
                {t.pagination.page
                  .replace('{current}', currentPage.toString())
                  .replace('{total}', totalPages.toString())}
              </div>
              <div className={`flex ${isRtl ? 'flex-row' : ''} gap-2`}>
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 active:scale-95"
                >
                  {isRtl ? <ArrowRight className="w-4 h-4" /> : <ArrowLeft className="w-4 h-4" />}
                  {t.pagination.previous}
                </button>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 active:scale-95"
                >
                  {t.pagination.next}
                  {isRtl ? <ArrowLeft className="w-4 h-4" /> : <ArrowRight className="w-4 h-4" />}
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* مودال إنشاء مرتجع */}
      <AnimatePresence>
        {isCreateModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => {
              setIsCreateModalOpen(false);
              dispatchReturnForm({ type: 'RESET' });
              setReturnErrors({});
            }}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 20 }}
              className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-5 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-semibold text-gray-900">{t.createReturn}</h2>
                  <button
                    onClick={() => {
                      setIsCreateModalOpen(false);
                      dispatchReturnForm({ type: 'RESET' });
                      setReturnErrors({});
                    }}
                    className="p-2 hover:bg-gray-100 rounded-xl transition-all duration-200 active:scale-95"
                  >
                    <X className="w-5 h-5 text-gray-500" />
                  </button>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-5">
                {returnErrors.form && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl">
                    <div className="flex items-center gap-2">
                      <AlertCircle className="w-4 h-4 text-red-600" />
                      <p className="text-red-600 text-sm">{returnErrors.form}</p>
                    </div>
                  </div>
                )}

                <div className="space-y-4 mb-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-medium text-gray-900">{t.items}</h3>
                    <button
                      type="button"
                      onClick={addItemToForm}
                      className="px-3.5 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-sm font-medium transition-all duration-200 flex items-center gap-1.5 active:scale-95"
                    >
                      <Plus className="w-4 h-4" />
                      {t.addItem}
                    </button>
                  </div>

                  {returnForm.items.length === 0 ? (
                    <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-xl">
                      <Package className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                      <p className="text-gray-500 text-sm">{t.noReturns}</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {returnForm.items.map((item, index) => {
                        const product = availableItems.find(p => p.productId === item.productId);
                        const unit = product?.displayUnit || t.unit;
                        const isKg = product && /كيلو|كجم|kg|kilo/i.test(product.unit);
                        
                        return (
                          <div key={index} className="bg-gray-50 border border-gray-200 rounded-xl p-4">
                            <div className="flex items-center justify-between mb-3">
                              <h4 className="font-medium text-gray-900">
                                {t.item} #{index + 1}
                              </h4>
                              <button
                                type="button"
                                onClick={() => removeItemFromForm(index)}
                                className="text-red-600 hover:text-red-800 transition-all duration-200 active:scale-95"
                              >
                                <XCircle className="w-5 h-5" />
                              </button>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  {t.product} *
                                </label>
                                <CustomDropdown
                                  value={item.productId}
                                  onChange={(value) => updateItemInForm(index, 'productId', value)}
                                  options={productOptions}
                                  placeholder={t.selectProduct}
                                />
                                {returnErrors[`item_${index}_productId`] && (
                                  <p className="text-red-500 text-xs mt-1">
                                    {returnErrors[`item_${index}_productId`]}
                                  </p>
                                )}
                              </div>

                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  {t.quantity} *
                                </label>
                                <QuantityInput
                                  value={item.quantity}
                                  onChange={(val) => updateItemInForm(index, 'quantity', val)}
                                  min={isKg ? 0.1 : 1}
                                  max={item.maxQuantity}
                                  unit={unit}
                                  disabled={!item.productId}
                                  t={t}
                                  isRtl={isRtl}
                                  productUnit={product?.unit || ''}
                                />
                                {item.maxQuantity > 0 && (
                                  <p className="text-xs text-gray-500 mt-1">
                                    {t.available}: {item.maxQuantity} {unit}
                                  </p>
                                )}
                                {returnErrors[`item_${index}_quantity`] && (
                                  <p className="text-red-500 text-xs mt-1">
                                    {returnErrors[`item_${index}_quantity`]}
                                  </p>
                                )}
                              </div>

                              <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  {t.reason} *
                                </label>
                                <CustomDropdown
                                  value={item.reason}
                                  onChange={(value) => updateItemInForm(index, 'reason', value)}
                                  options={reasonOptions.map(opt => ({ value: opt.value, label: opt.label }))}
                                  placeholder={t.selectReason}
                                />
                                {returnErrors[`item_${index}_reason`] && (
                                  <p className="text-red-500 text-xs mt-1">
                                    {returnErrors[`item_${index}_reason`]}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {returnErrors.items && (
                    <p className="text-red-500 text-sm">{returnErrors.items}</p>
                  )}
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t.notesLabel}
                  </label>
                  <textarea
                    value={returnForm.notes}
                    onChange={(e) => dispatchReturnForm({ type: 'SET_NOTES', payload: e.target.value })}
                    placeholder={t.notesPlaceholder}
                    rows={3}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent resize-none text-sm"
                  />
                </div>

                {returnForm.items.length > 0 && (
                  <div className="bg-gray-50 rounded-xl p-4 mb-4">
                    <h4 className="font-medium text-gray-900 mb-2">{t.summary}</h4>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">{t.itemsCount}:</span>
                        <span className="font-medium">{returnForm.items.length}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">{t.totalQuantity}:</span>
                        <span className="font-medium">
                          {returnForm.items.reduce((sum, item) => sum + item.quantity, 0)}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="p-5 border-t border-gray-200 bg-gray-50">
                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setIsCreateModalOpen(false);
                      dispatchReturnForm({ type: 'RESET' });
                      setReturnErrors({});
                    }}
                    className="px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-medium transition-all duration-200 active:scale-95"
                  >
                    {t.common.cancel}
                  </button>
                  <button
                    type="button"
                    onClick={() => createReturnMutation.mutate()}
                    disabled={createReturnMutation.isLoading || returnForm.items.length === 0}
                    className="px-4 py-2.5 bg-amber-600 hover:bg-amber-700 text-white rounded-xl font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 active:scale-95"
                  >
                    {createReturnMutation.isLoading ? (
                      <>
                        <RefreshCw className="w-4 h-4 animate-spin" />
                        {t.common.submitting}
                      </>
                    ) : (
                      t.submitReturn
                    )}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* مودال عرض المرتجع */}
      <AnimatePresence>
        {isViewModalOpen && selectedReturn && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => setIsViewModalOpen(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 20 }}
              className="bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-5 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-semibold text-gray-900">{t.viewReturn}</h2>
                  <button
                    onClick={() => setIsViewModalOpen(false)}
                    className="p-2 hover:bg-gray-100 rounded-xl transition-all duration-200 active:scale-95"
                  >
                    <X className="w-5 h-5 text-gray-500" />
                  </button>
                </div>
              </div>
              
              <div className="flex-1 overflow-y-auto p-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  <div>
                    <p className="text-sm text-gray-500">{t.returnNumber}</p>
                    <p className="text-lg font-semibold text-gray-900">{selectedReturn.returnNumber}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">{t.statusLabel}</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {t.status[selectedReturn.status as keyof typeof t.status]}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">{t.branch}</p>
                    <p className="text-lg font-semibold text-gray-900">{selectedReturn.branch?.displayName}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">{t.createdAt}</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {formatDateWithoutSeconds(selectedReturn.createdAt)}
                    </p>
                  </div>
                </div>

                <div className="overflow-x-auto mb-6">
                  <table className={`w-full border-collapse text-sm ${isRtl ? 'text-right' : 'text-left'}`}>
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t.product}
                        </th>
                        <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t.originalQuantity}
                        </th>
                        <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t.approvedQuantity}
                        </th>
                        <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t.reason}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {selectedReturn.items.map((item, index) => {
                        const approvedQty = item.approvedQuantity || 0;
                        const isIncreased = approvedQty > item.quantity;
                        
                        return (
                          <tr key={index} className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm text-gray-900">
                              <div className="flex items-center gap-2">
                                {item.product.displayName}
                                {isIncreased && (
                                  <span className="text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded">
                                    +{(approvedQty - item.quantity).toFixed(1)}
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600">
                              {item.quantity} {item.product.displayUnit}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600">
                              <div className="flex items-center gap-2">
                                <span>{approvedQty} {item.product.displayUnit}</span>
                                {isIncreased && (
                                  <Layers className="w-3 h-3 text-purple-600" />
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600">
                              {isRtl ? item.reason : item.reasonEn}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {selectedReturn.notes && (
                  <div className="mb-4 p-4 bg-amber-50 rounded-xl border border-amber-200">
                    <h4 className="text-sm font-medium text-amber-800 mb-1">{t.notesLabel}</h4>
                    <p className="text-sm text-amber-700">{selectedReturn.notes}</p>
                  </div>
                )}

                {selectedReturn.reviewNotes && (
                  <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
                    <h4 className="text-sm font-medium text-blue-800 mb-1">{t.reviewNotes}</h4>
                    <p className="text-sm text-blue-700">{selectedReturn.reviewNotes}</p>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* مودال الموافقة على المرتجع */}
      <ApproveReturnModal
        isOpen={isApproveModalOpen}
        onClose={() => {
          setIsApproveModalOpen(false);
          setSelectedReturn(null);
          setReviewNotes('');
        }}
        selectedReturn={selectedReturn}
        t={t}
        isRtl={isRtl}
        onSubmit={handleApproveSubmit}
        isSubmitting={updateReturnStatusMutation.isLoading}
      />

      {/* مودال تأكيد الموافقة بالقوة */}
      <ForceApproveModal
        isOpen={showForceApproveModal}
        onClose={() => {
          setShowForceApproveModal(false);
          setApprovalError(null);
          setPendingApproval(null);
          setIsApproveModalOpen(true); // العودة لمودال الموافقة
        }}
        onConfirm={(forceApprove) => {
          if (pendingApproval) {
            // إعادة محاولة الموافقة مع الخيار forceApprove
            handleApproveSubmit(
              pendingApproval.approvedItems,
              pendingApproval.reviewNotes,
              pendingApproval.status,
              forceApprove
            );
          }
        }}
        t={t}
        isRtl={isRtl}
        errorMessage={approvalError}
      />
    </div>
  );
};

export default BranchReturns;